import { Input, InputTextArea, Row, Select, useForm } from '@/components/forms'
import { createModel } from '@/lib/data/model'
import { NodeProps } from '@xyflow/react'
import { InlineHandles, WorkerLabeledHandle } from '../handles'
import { useWorker } from '../hooks'
import { MemoizedWorker } from '../memoizedworkers'
import { NodeLayout } from './node'
import { useEffect, useMemo, useState } from 'react'
import { workerRegistry } from '@/lib/agents/registry'
import { app } from '@/lib/app'
import { useTeamStore } from '@/lib/hooks/useTeam'
import { ConditionHandler } from '../condition'
import { Unplug } from 'lucide-react'
import { JsonEditor } from 'json-edit-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Maximize2, FileJson, AlertCircle, Copy } from 'lucide-react'

const { apiConnector } = workerRegistry
apiConnector.icon = Unplug

const authTypeOptions = [
  { value: "none", label: "None" },
  { value: "basic", label: "Basic Auth" },
  { value: "bearer", label: "Bearer Token" },
  { value: "api_key", label: "API Key" },
  { value: "oauth2_client_credentials", label: "OAuth 2.0 Client Credentials" },
]

const methodOptions = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
]

const model = createModel({
  fields: {
    endpoint: { title: "Endpoint URL", type: "string" },
    method: { title: "Method", type: "string", list: methodOptions },
    toolName: { title: "Tool Name", type: "string" },
    toolDescription: { title: "Tool Description", type: "string" },
    params: { title: "Query Parameters (JSON)", type: "string" },
    paramsSchema: { title: "Tool Parameters Schema (JSON)", type: "string" },
    headers: { title: "Additional Headers", type: "string" },
    timeout: { title: "Timeout (ms)", type: "number" },
    authType: { title: "Auth Type", type: "string", list: authTypeOptions },
    username: { title: "Username", type: "string" },
    selectedKeyName: { title: "Stored Key Name", type: "string" },
    oauth2TokenUrl: { title: "OAuth2 Token URL", type: "string" },
    oauth2ClientId: { title: "OAuth2 Client ID", type: "string" },
    oauth2Scope: { title: "OAuth2 Scope", type: "string" },
    rateLimitPerMinute: { title: "Rate Limit (req/min)", type: "number" },
    maxRetries: { title: "Max Retries", type: "number" },
  }
})

function ResponseViewer({ response }: { response: string }) {
  const [isOpen, setIsOpen] = useState(false)
  let parsedResponse: unknown = null
  let isValidJson = false
  try {
    parsedResponse = JSON.parse(response)
    isValidJson = true
  } catch { /* not JSON */ }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-1 w-full">
          {isValidJson ? <FileJson size={14} /> : <AlertCircle size={14} />}
          <span>View Response</span>
          <Maximize2 size={14} className="ml-auto" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl w-[90vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              {isValidJson ? <FileJson size={16} className="text-green-600" /> : <AlertCircle size={16} />}
              <span>{isValidJson ? 'JSON Response' : 'Response'}</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-1"
              onClick={() => navigator.clipboard.writeText(response)}
            >
              <Copy size={14} />
              <span>Copy</span>
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-auto bg-slate-50 rounded border">
          {isValidJson ? (
            <div className="p-4">
              <JsonEditor data={parsedResponse} setData={() => {}} viewOnly enableClipboard />
            </div>
          ) : (
            <pre className="text-sm whitespace-pre-wrap p-4">{response}</pre>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Parameters({ worker }: { worker: ApiConnectorWorker }) {
  const { selectedTeam } = useTeamStore()
  const [authType, setAuthType] = useState(worker.parameters.authType || "none")
  const [apiKeys, setApiKeys] = useState<APIKeys>({})

  useEffect(() => {
    app.fetchAPIkeys(selectedTeam?.id).then(keys => setApiKeys(keys || {}))
  }, [selectedTeam?.id])

  const globalApiKeyOptions = useMemo(() =>
    Object.keys(apiKeys).map(name => ({ value: name, label: name })),
    [apiKeys]
  )

  const { form, m, watch, setValue } = useForm(model, {
    values: {
      endpoint: worker.parameters.endpoint || '',
      method: (worker.parameters.method || 'GET').toUpperCase(),
      toolName: worker.parameters.toolName || 'api_call',
      toolDescription: worker.parameters.toolDescription || 'Call an external HTTP API',
      params: worker.parameters.params || '{}',
      paramsSchema: worker.parameters.paramsSchema || '[]',
      headers: worker.parameters.headers || '{}',
      timeout: worker.parameters.timeout || 10000,
      authType: worker.parameters.authType || 'none',
      username: worker.parameters.username || '',
      selectedKeyName: worker.parameters.selectedKeyName || '',
      oauth2TokenUrl: worker.parameters.oauth2TokenUrl || '',
      oauth2ClientId: worker.parameters.oauth2ClientId || '',
      oauth2Scope: worker.parameters.oauth2Scope || '',
      rateLimitPerMinute: worker.parameters.rateLimitPerMinute ?? 60,
      maxRetries: worker.parameters.maxRetries ?? 2,
    }
  })

  if (m.selectedKeyName) m.selectedKeyName.list = globalApiKeyOptions

  watch((value, { name }) => {
    const w = worker.parameters
    if (name === "endpoint") w.endpoint = value.endpoint
    if (name === "method") w.method = value.method
    if (name === "toolName") w.toolName = value.toolName
    if (name === "toolDescription") w.toolDescription = value.toolDescription
    if (name === "params") w.params = value.params
    if (name === "paramsSchema") w.paramsSchema = value.paramsSchema
    if (name === "headers") w.headers = value.headers
    if (name === "timeout") w.timeout = value.timeout
    if (name === "authType") {
      const v = value.authType || 'none'
      setAuthType(v)
      w.authType = v
      if (v === 'none') { setValue('selectedKeyName', ''); w.selectedKeyName = '' }
    }
    if (name === "username") w.username = value.username
    if (name === "selectedKeyName") w.selectedKeyName = value.selectedKeyName
    if (name === "oauth2TokenUrl") w.oauth2TokenUrl = value.oauth2TokenUrl
    if (name === "oauth2ClientId") w.oauth2ClientId = value.oauth2ClientId
    if (name === "oauth2Scope") w.oauth2Scope = value.oauth2Scope
    if (name === "rateLimitPerMinute") w.rateLimitPerMinute = value.rateLimitPerMinute
    if (name === "maxRetries") w.maxRetries = value.maxRetries
  })

  return (
    <form.context>
      <div className='p-2 -mt-2 nodrag w-full space-y-3'>
        <h3 className="font-semibold text-sm mb-2">Request Configuration</h3>
        <Row>
          <Input field={m.endpoint} span={10} placeholder="https://api.example.com/resource" />
          <Select field={m.method} span={2} />
        </Row>

        <h3 className="font-semibold text-sm mb-2">Tool Configuration</h3>
        <p className="text-xs text-muted-foreground">
          Connect the Tool handle to an LLM Agent to make this API callable as a tool.
        </p>
        <Row>
          <Input field={m.toolName} span={6} placeholder="get_weather" />
          <Input field={m.toolDescription} span={6} placeholder="Get weather for a location" />
        </Row>

        <h3 className="font-semibold text-sm mb-2">Authentication</h3>
        <Row>
          <Select field={m.authType} span={12} />
        </Row>
        {authType === 'basic' && (
          <>
            <Row><Input field={m.username} span={12} placeholder="username or email" /></Row>
            <Row><Select field={m.selectedKeyName} span={12} placeholder="Select Stored Key for Password..." /></Row>
          </>
        )}
        {(authType === 'bearer' || authType === 'api_key') && (
          <Row>
            <Select field={m.selectedKeyName} span={12} placeholder={authType === 'bearer' ? "Select Stored Key for Token..." : "Select Stored API Key..."} />
          </Row>
        )}
        {authType === 'oauth2_client_credentials' && (
          <>
            <p className="text-xs text-muted-foreground">
              Exchange client ID and secret for an access token (e.g. Databricks, Azure AD, Okta).
            </p>
            <Row><Input field={m.oauth2TokenUrl} span={12} placeholder="https://example.com/oidc/v1/token" /></Row>
            <Row><Input field={m.oauth2ClientId} span={12} placeholder="OAuth2 Client ID" /></Row>
            <Row><Select field={m.selectedKeyName} span={12} placeholder="Select Stored Key for Client Secret..." /></Row>
            <Row><Input field={m.oauth2Scope} span={12} placeholder="Scope (optional, e.g. all-apis)" /></Row>
          </>
        )}

        <h3 className="font-semibold text-sm mt-3 mb-2">Parameters & Headers</h3>
        <Row>
          <InputTextArea field={m.params} span={12} placeholder='{"key": "value"}' />
        </Row>
        <p className="text-xs text-muted-foreground px-2">
          GET: params as query string. POST/PUT/PATCH: merged with tool args into JSON body. Use {"{param}"} in URL for path params.
        </p>
        <Row>
          <InputTextArea field={m.paramsSchema} span={12} placeholder='[{"name":"location","type":"string","description":"City name","required":true}]' />
        </Row>
        <p className="text-xs text-muted-foreground p-2 border rounded bg-slate-50">
          Tool params schema: JSON array of {"{name, type, description?, required?}"}. Types: string, number, boolean, array, object.
        </p>
        <Row>
          <InputTextArea field={m.headers} span={12} placeholder='{"Content-Type": "application/json"}' />
        </Row>

        <h3 className="font-semibold text-sm mt-3 mb-2">Options</h3>
        <Row>
          <Input field={m.timeout} type="number" span={4} min={1000} max={60000} step={1000} />
          <Input field={m.rateLimitPerMinute} type="number" span={4} min={0} placeholder="60" />
          <Input field={m.maxRetries} type="number" span={4} min={0} max={5} />
        </Row>
      </div>
    </form.context>
  )
}

export function ApiConnectorNode(props: NodeProps) {
  const worker = useWorker<ApiConnectorWorker>(props.id)

  return (
    <NodeLayout worker={worker} resizable minHeight={500}>
      <div className='flex flex-col h-full'>
        <InlineHandles>
          <WorkerLabeledHandle handler={worker.fields.body} />
          <WorkerLabeledHandle handler={worker.fields.response} />
        </InlineHandles>
        <InlineHandles>
          <WorkerLabeledHandle handler={worker.fields.endpointUrlInput} />
          <WorkerLabeledHandle handler={worker.fields.error} />
        </InlineHandles>
        <WorkerLabeledHandle handler={worker.fields.tool} />

        <MemoizedWorker worker={worker}>
          <Parameters worker={worker} />
        </MemoizedWorker>

        <ConditionHandler />

        {worker.fields.response.value && (
          <div className="mx-2 mt-2 border border-green-200 bg-green-50 rounded nodrag">
            <div className="text-xs font-semibold p-2 border-b border-green-200">
              <div className="flex items-center justify-between">
                <span>Response:</span>
                <span className="text-xs text-green-600">
                  {worker.fields.response.value.length} characters
                </span>
              </div>
            </div>
            <div className="p-2">
              <ResponseViewer response={worker.fields.response.value} />
            </div>
          </div>
        )}

        {worker.fields.error.value && (
          <div className="mx-2 mt-2 p-2 border border-red-200 bg-red-50 rounded overflow-auto nodrag" style={{ maxHeight: "150px" }}>
            <div className="text-xs font-semibold mb-1 text-red-700">Error:</div>
            <pre className="text-xs whitespace-pre-wrap text-red-600">{worker.fields.error.value}</pre>
          </div>
        )}
      </div>
    </NodeLayout>
  )
}
